import { Component, ElementRef, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { IconComponent } from 'app/shared/components/icon/icon.component';
import { NAVIGATION } from './navigation';

@Component({
  selector: 'ez-navbar',
  imports: [RouterLink, RouterLinkActive, IconComponent],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  host: {
    '(document:click)': 'closeOnOutsideClick($event)',
    '(document:keydown.escape)': 'closeOnEscape()',
    '(focusout)': 'closeOnFocusLeave($event)',
  },
})
export class NavbarComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly menus = NAVIGATION;
  protected readonly openMenuId = signal<string | null>(null);

  protected isOpen(menuId: string): boolean {
    return this.openMenuId() === menuId;
  }

  protected panelId(menuId: string): string {
    return `${menuId}-panel`;
  }

  protected toggle(menuId: string): void {
    this.openMenuId.update((openId) => (openId === menuId ? null : menuId));
  }

  protected closeOnEscape(): void {
    this.closeAndFocusMenuButton();
  }

  protected closeOnFocusLeave(event: FocusEvent): void {
    const menuItem = this.openMenuButton()?.closest('li');
    const nextFocus = event.relatedTarget as Node | null;
    if (menuItem && nextFocus && !menuItem.contains(nextFocus)) {
      this.close();
    }
  }

  protected closeOnLinkClick(event: MouseEvent): void {
    if (!this.isModifiedClick(event)) {
      this.closeAndFocusMenuButton();
    }
  }

  protected closeOnOutsideClick(event: Event): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  private openMenuButton(): HTMLElement | null {
    const openId = this.openMenuId();
    return openId === null
      ? null
      : this.host.nativeElement.querySelector<HTMLElement>(`[aria-controls="${this.panelId(openId)}"]`);
  }

  private isModifiedClick(event: MouseEvent): boolean {
    const isPrimaryButton = event.button === 0;
    const hasModifierKey = event.ctrlKey || event.metaKey || event.shiftKey;
    return hasModifierKey || !isPrimaryButton;
  }

  private closeAndFocusMenuButton(): void {
    const button = this.openMenuButton();
    this.close();
    button?.focus();
  }

  private close(): void {
    this.openMenuId.set(null);
  }
}
